/**
 * GoogleMap Component
 * Displays Google Maps with markers and tracking
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, MapPin, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom marker icons SVG paths
const MARKER_COLORS = {
  specialist: '#3B82F6', // blue
  order_urgent: '#EF4444', // red
  order_high: '#F97316', // orange
  order_normal: '#3B82F6', // blue
  order_low: '#6B7280', // gray
  selected: '#E63946' // brand red
};

export const GoogleMap = ({
  apiKey,
  center = { lat: 4.7110, lng: -74.0721 }, // Bogotá default
  zoom = 12,
  markers = [],
  specialists = [],
  orders = [],
  selectedSpecialist = null,
  selectedOrder = null,
  onMarkerClick,
  onMapClick,
  showTraffic = false,
  className = '',
  'data-testid': testId = 'google-map'
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [mapsApiKey, setMapsApiKey] = useState(apiKey);

  // Fetch API key from config if not provided
  useEffect(() => {
    const fetchApiKey = async () => {
      if (apiKey) {
        setMapsApiKey(apiKey);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/config/maps`);
        if (response.data?.api_key) {
          setMapsApiKey(response.data.api_key);
        } else {
          setError('Google Maps API Key no configurada');
        }
      } catch (err) {
        console.error('Error fetching maps config:', err);
        setError('Error al cargar configuración de mapas');
      }
    };

    fetchApiKey();
  }, [apiKey]);

  // Load Google Maps script
  useEffect(() => {
    if (!mapsApiKey) return;

    // Check if already loaded
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      setError('Error al cargar Google Maps');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup is handled by browser
    };
  }, [mapsApiKey]);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const mapOptions = {
      center: center,
      zoom: zoom,
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.TOP_RIGHT
      },
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    };

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);

    // Add traffic layer if enabled
    if (showTraffic) {
      const trafficLayer = new window.google.maps.TrafficLayer();
      trafficLayer.setMap(mapInstanceRef.current);
    }

    // Map click handler
    if (onMapClick) {
      mapInstanceRef.current.addListener('click', (e) => {
        onMapClick({
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        });
      });
    }
  }, [isLoaded, center, zoom, showTraffic, onMapClick]);

  // Create custom marker icon
  const createMarkerIcon = useCallback((color, isSelected = false) => {
    if (!window.google) return null;

    const size = isSelected ? 40 : 32;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3" fill="white"/>
      </svg>
    `;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size)
    };
  }, []);

  // Update markers
  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasMarkers = false;

    // Add specialist markers
    specialists.forEach(specialist => {
      if (!specialist.current_location) return;

      const position = {
        lat: specialist.current_location.latitude,
        lng: specialist.current_location.longitude
      };

      const isSelected = selectedSpecialist?.id === specialist.id;
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        icon: createMarkerIcon(isSelected ? MARKER_COLORS.selected : MARKER_COLORS.specialist, isSelected),
        title: `${specialist.first_name} ${specialist.last_name}`,
        zIndex: isSelected ? 1000 : 100
      });

      // Info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 150px;">
            <p style="font-weight: 600; margin: 0;">${specialist.first_name} ${specialist.last_name}</p>
            <p style="font-size: 12px; color: #666; margin: 4px 0 0 0;">${specialist.specialist_type}</p>
            <p style="font-size: 11px; color: #888; margin: 4px 0 0 0;">
              Última actualización: ${new Date(specialist.current_location.timestamp).toLocaleTimeString('es-CO')}
            </p>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, marker);
        if (onMarkerClick) {
          onMarkerClick({ type: 'specialist', data: specialist });
        }
      });

      markersRef.current.push(marker);
      bounds.extend(position);
      hasMarkers = true;
    });

    // Add order markers
    orders.forEach(order => {
      if (!order.service_latitude || !order.service_longitude) return;

      const position = {
        lat: order.service_latitude,
        lng: order.service_longitude
      };

      const priorityColor = MARKER_COLORS[`order_${order.priority}`] || MARKER_COLORS.order_normal;
      const isSelected = selectedOrder?.id === order.id;

      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        icon: createMarkerIcon(isSelected ? MARKER_COLORS.selected : priorityColor, isSelected),
        title: order.patient_name || 'Orden',
        zIndex: isSelected ? 1000 : (order.priority === 'urgent' ? 500 : 50)
      });

      // Info window for orders
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 180px;">
            <p style="font-weight: 600; margin: 0;">${order.patient_name || 'Paciente'}</p>
            <p style="font-size: 12px; color: #666; margin: 4px 0;">${order.service_address || 'Sin dirección'}</p>
            <div style="display: flex; gap: 8px; margin-top: 8px;">
              <span style="font-size: 11px; padding: 2px 6px; background: ${priorityColor}; color: white; border-radius: 4px;">
                ${order.priority}
              </span>
              <span style="font-size: 11px; padding: 2px 6px; background: #E5E7EB; color: #374151; border-radius: 4px;">
                ${order.status}
              </span>
            </div>
            ${order.scheduled_time ? `<p style="font-size: 11px; color: #888; margin: 8px 0 0 0;">Hora: ${order.scheduled_time}</p>` : ''}
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, marker);
        if (onMarkerClick) {
          onMarkerClick({ type: 'order', data: order });
        }
      });

      markersRef.current.push(marker);
      bounds.extend(position);
      hasMarkers = true;
    });

    // Add generic markers
    markers.forEach((markerData, index) => {
      if (!markerData.lat || !markerData.lng) return;

      const position = { lat: markerData.lat, lng: markerData.lng };
      
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        icon: createMarkerIcon(markerData.color || MARKER_COLORS.selected),
        title: markerData.title || ''
      });

      if (markerData.content) {
        const infoWindow = new window.google.maps.InfoWindow({
          content: markerData.content
        });

        marker.addListener('click', () => {
          infoWindow.open(mapInstanceRef.current, marker);
        });
      }

      markersRef.current.push(marker);
      bounds.extend(position);
      hasMarkers = true;
    });

    // Fit bounds if we have markers
    if (hasMarkers && markersRef.current.length > 1) {
      mapInstanceRef.current.fitBounds(bounds);
      // Limit zoom level
      const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'idle', () => {
        if (mapInstanceRef.current.getZoom() > 16) {
          mapInstanceRef.current.setZoom(16);
        }
        window.google.maps.event.removeListener(listener);
      });
    } else if (hasMarkers && markersRef.current.length === 1) {
      mapInstanceRef.current.setCenter(markersRef.current[0].getPosition());
      mapInstanceRef.current.setZoom(15);
    }
  }, [isLoaded, specialists, orders, markers, selectedSpecialist, selectedOrder, createMarkerIcon, onMarkerClick]);

  // Error state
  if (error) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ minHeight: 400 }}
        data-testid={testId}
      >
        <div className="text-center text-gray-500 p-8">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
          <p className="font-medium">{error}</p>
          <p className="text-sm mt-2">Configure la API Key en Configuración → Mapas</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!isLoaded) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ minHeight: 400 }}
        data-testid={testId}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946] mx-auto mb-3" />
          <p className="text-gray-500">Cargando mapa...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef}
      className={`rounded-lg overflow-hidden ${className}`}
      style={{ minHeight: 400 }}
      data-testid={testId}
    />
  );
};

export default GoogleMap;
